import { ConnectError } from '@bufbuild/connect';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate, useSearchParams } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { UTMParameters } from '@/shared/api/protocol-ts/model/dto_marketing_pb';
import { UserConsents } from '@/shared/api/protocol-ts/model/dto_user_pb';
import {
  CLUSTER,
  LocalStorageKey,
  PATHS,
  UserBadRequestError,
} from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import {
  getHubspotUserToken,
  normalizeEmail,
  safeJSONParse,
} from '@/shared/lib';

import { authModel } from '@/entities/auth';
import {
  getBillingZoneByCountry,
  getLinksAgreementsByBillingZone,
} from '@/entities/billing';
import { ModalID, modalModel } from '@/entities/modal';

import {
  signUpFormSchema,
  signUpViaOrganizationInviteFormSchema,
  signUpViaSharedPatientFormSchema,
} from '../config/formConfig';
import {
  SignUpFormDataType,
  SignUpHandleType,
  SignUpViaOrganizationInviteHandleType,
  SignUpViaSharedPatientHandleType,
} from '../config/types';

export const useSignUp = (invitationToken: string) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  const [selectCountry, setSelectCountry] = useState('');

  const billingZoneByCountry = getBillingZoneByCountry(selectCountry);
  const billingZoneForAgreements =
    billingZoneByCountry ?? (CLUSTER === 'ru' ? 'CIS' : undefined);

  const userLinksAgreements = getLinksAgreementsByBillingZone({
    country: selectCountry,
    billingZone: billingZoneForAgreements,
  });

  // set UTM
  useEffect(() => {
    if (searchParams) {
      const UTMSource = searchParams.get('utm_source') ?? undefined;
      const UTMMedium = searchParams.get('utm_medium') ?? undefined;
      const UTMCampaign = searchParams.get('utm_campaign') ?? undefined;
      const UTMTerm = searchParams.get('utm_term') ?? undefined;
      const UTMContent = searchParams.get('utm_content') ?? undefined;

      if (UTMSource && UTMMedium && UTMCampaign && UTMTerm && UTMContent) {
        const utmParameters = {
          UTMSource,
          UTMMedium,
          UTMCampaign,
          UTMTerm,
          UTMContent,
        } as UTMParameters;

        localStorage.setItem(
          LocalStorageKey.UTMParameters,
          JSON.stringify(utmParameters),
        );
      }
    }
  }, [searchParams]);

  const [isInviteFromOrganization, setIsInviteFromOrganization] =
    useState(false);

  const [invitingOrganizationName, setInvitingOrganizationName] = useState('');

  const [invitingOrganizationLanguage, setInvitingOrganizationLanguage] =
    useState('');

  const [isInviteFromDoctor, setIsInviteFromDoctor] = useState(false);

  const [sharedDoctorName, setSharedDoctorName] = useState('');

  const [isInviteFromSalesman, setIsInviteFromSalesman] = useState(false);

  const schema = useMemo(() => {
    if (isInviteFromOrganization) {
      return signUpViaOrganizationInviteFormSchema;
    }

    if (isInviteFromDoctor) {
      return signUpViaSharedPatientFormSchema;
    }

    return signUpFormSchema;
  }, [isInviteFromOrganization, isInviteFromDoctor]);

  const hookFormData = useForm<SignUpFormDataType>({
    resolver: yupResolver<SignUpFormDataType>(schema),
    mode: 'onBlur',
  });

  const { setError } = hookFormData;

  const errorHandler = (error: ConnectError) => {
    const parsedMessage = safeJSONParse(error?.rawMessage);

    if (parsedMessage?.code === UserBadRequestError.EMAIL_TAKEN) {
      const errorMessage = formatMessage(
        {
          id: 'signUp.errors.emailTakenError',
          defaultMessage:
            'There is already an account with this email address. <a>Sign in</a> instead.',
        },
        {
          a: (chunks: ReactNode) => (
            <a style={{ color: 'var(--purpleflat)' }} href={PATHS.signIn}>
              {chunks}
            </a>
          ),
        },
      ) as string;

      setError('email', {
        message: errorMessage,
        type: UserBadRequestError.EMAIL_TAKEN,
      });
    }
  };

  const checkInviteToken = async (inviteToken: string) => {
    try {
      const { Info } = await dispatch(
        authModel.thunks.checkInviteToken({ Token: inviteToken }),
      ).unwrap();

      if (Info.case === 'InvitationInfo') {
        if (Info.value.Result.case === 'OrganizationStaff') {
          setIsInviteFromOrganization(true);

          setInvitingOrganizationName(Info.value.Organization?.Name ?? '');

          setInvitingOrganizationLanguage(
            Info.value.Organization?.Settings?.Language ?? '',
          );

          setSelectCountry(Info.value.Organization?.Contacts?.Country ?? '');
        }

        if (Info.value.Result.case === 'Sharing') {
          setIsInviteFromDoctor(true);

          setSharedDoctorName(
            `${Info.value.Result.value.TargetDetails?.SenderFirstName} ${Info.value.Result.value.TargetDetails?.SenderLastName}`,
          );
        }
      }

      if (Info.case === 'SignUpViaSalesmanInfo') {
        setIsInviteFromSalesman(true);
      }
    } catch (error) {
      const connectError = ConnectError.from(error);
      errorHandler(connectError);
    }
  };

  // check invite token
  useEffect(() => {
    if (invitationToken) {
      checkInviteToken(invitationToken);
    }
  }, [invitationToken, checkInviteToken]);

  const saveSignUpFormDataHandle = (name: string, value: string) => {
    const formData = localStorage.getItem(LocalStorageKey.signUpFormData);

    const updatedFormData = formData
      ? { ...JSON.parse(formData), [name]: value }
      : { [name]: value };

    localStorage.setItem(
      LocalStorageKey.signUpFormData,
      JSON.stringify(updatedFormData),
    );
  };

  const signUpHandle = async ({
    email: rawEmail,
    country,
    language,
    firstName,
    lastName,
    phoneNumber,
  }: SignUpHandleType) => {
    setIsLoading(true);
    const email = normalizeEmail(rawEmail);

    try {
      const signUpResponse = await dispatch(
        authModel.thunks.signUp({
          Email: email,
          Country: country,
          Language: language,
          FirstName: firstName,
          LastName: lastName,
          PhoneNumber: phoneNumber ?? '',
        }),
      ).unwrap();

      if (signUpResponse.Status === AuthState_AuthStatus.IN_PROGRESS) {
        navigate(PATHS.confirmEmail, {
          state: {
            email,
          },
        });
      }

      localStorage.removeItem(LocalStorageKey.signUpFormData);
    } catch (error) {
      const connectError = ConnectError.from(error);
      errorHandler(connectError);
    }

    setIsLoading(false);
  };

  const signUpViaOrganizationInviteHandle = async ({
    firstName,
    lastName,
    password,
    language,
    marketingCommunicationConsent,
  }: SignUpViaOrganizationInviteHandleType) => {
    setIsLoading(true);

    try {
      const signUpViaOrganizationInviteResponse = await dispatch(
        authModel.thunks.signUpViaOrganizationInvite({
          InviteToken: invitationToken,
          FirstName: firstName,
          LastName: lastName,
          Password: password,
          Language: language,
          Consents: new UserConsents({
            MarketingConsent: marketingCommunicationConsent || false,
          }),
          HubspotUTK: getHubspotUserToken(),
        }),
      ).unwrap();

      if (
        signUpViaOrganizationInviteResponse.Status ===
        AuthState_AuthStatus.SUCCESSFUL
      ) {
        dispatch(
          modalModel.actions.openModal({
            modalID: ModalID.CongratsModal,
            data: {
              redirectTo: PATHS.patients,
              message: isInviteFromOrganization
                ? formatMessage(
                    {
                      id: 'signUp.inviteFromOrganization.attachedToOrganization',
                      defaultMessage: `Now you are attached to organization "{invitingOrganizationName}"`,
                    },
                    { invitingOrganizationName },
                  )
                : '',
            },
          }),
        );
      }

      localStorage.removeItem(LocalStorageKey.signUpFormData);
    } catch (error) {
      const connectError = ConnectError.from(error);
      errorHandler(connectError);
    }

    setIsLoading(false);
  };

  const signUpViaSharedPatientHandle = async ({
    firstName,
    lastName,
    password,
    language,
    country,
    marketingCommunicationConsent,
  }: SignUpViaSharedPatientHandleType) => {
    setIsLoading(true);

    try {
      const signUpViaSharedPatientResponse = await dispatch(
        authModel.thunks.signUpViaSharedPatient({
          InviteToken: invitationToken,
          FirstName: firstName,
          LastName: lastName,
          Password: password,
          Language: language,
          Country: country,
          Consents: new UserConsents({
            MarketingConsent: marketingCommunicationConsent || false,
          }),
          HubspotUTK: getHubspotUserToken(),
        }),
      ).unwrap();

      if (
        signUpViaSharedPatientResponse.AuthState?.Status ===
        AuthState_AuthStatus.SUCCESSFUL
      ) {
        dispatch(
          modalModel.actions.openModal({
            modalID: ModalID.CongratsModal,
            data: {
              redirectTo: generatePath(PATHS.organization_invite, {
                token: invitationToken,
              }),
            },
          }),
        );
      }

      localStorage.removeItem(LocalStorageKey.signUpFormData);
    } catch (error) {
      const connectError = ConnectError.from(error);
      errorHandler(connectError);
    }

    setIsLoading(false);
  };

  const navigateToFinishRegistration = () =>
    navigate(PATHS.finishRegistration, {
      state: { isInviteFromSalesman, invitationToken },
    });

  return {
    isInviteFromOrganization,
    isInviteFromDoctor,
    isInviteFromSalesman,
    isLoading,
    invitingOrganizationName,
    invitingOrganizationLanguage,
    sharedDoctorName,
    userLinksAgreements,
    signUpHandle,
    signUpViaOrganizationInviteHandle,
    signUpViaSharedPatientHandle,
    saveSignUpFormDataHandle,
    setSelectCountry,
    navigateToFinishRegistration,
    hookFormData,
  };
};
